import { useCallback, useEffect, useMemo, useState } from 'react';
import { bangkokDate, emptySale, type Branch, type DailySale, type Region } from './domain';
import { clearStore, loadLocalState, putValue, setSetting, setting } from './db';
import { flushOutbox, getDeviceIdentity, recordEvent } from './sync';
import { registerCurrentDevice } from './device';
import { prepareReportSales, ReportSubmissionError, sendReport, type ReportMode } from './reportSubmission';

export function useAppStore() {
  const [ready, setReady] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [sales, setSales] = useState<DailySale[]>([]);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const today = useMemo(() => bangkokDate(), []);
  const reportSettingKey = `report-submitted:${today}`;

  useEffect(() => {
    void Promise.all([loadLocalState(), setting(reportSettingKey)]).then(([state, submitted]) => {
      setBranches(state.branches.sort((a, b) => a.sortOrder - b.sortOrder));
      setSales(state.sales);
      setReportSubmitted(submitted === '1');
      setReady(true);
      void flushOutbox();
      void registerCurrentDevice();
    });
    const online = () => { void flushOutbox(); void registerCurrentDevice(); };
    window.addEventListener('online', online);
    const visible = () => { if (!document.hidden) { void flushOutbox(); void registerCurrentDevice(); } };
    document.addEventListener('visibilitychange', visible);
    return () => { window.removeEventListener('online', online); document.removeEventListener('visibilitychange', visible); };
  }, []);

  const activeBranches = useMemo(() => branches.filter((branch) => branch.isActive).sort((a, b) => a.sortOrder - b.sortOrder), [branches]);

  const addBranch = useCallback(async (name: string, region: Region) => {
    const now = new Date().toISOString();
    const branch: Branch = { id: crypto.randomUUID(), name: name.trim(), region, sortOrder: activeBranches.length + 1, isActive: true, createdAt: now, updatedAt: now };
    await putValue('branches', branch); setBranches((list) => [...list, branch]);
    await recordEvent('branch_created', { name: branch.name, region }, branch.id);
  }, [activeBranches.length]);

  const updateBranch = useCallback(async (branch: Branch) => {
    const next = { ...branch, name: branch.name.trim(), updatedAt: new Date().toISOString() };
    await putValue('branches', next); setBranches((list) => list.map((item) => item.id === next.id ? next : item));
    await recordEvent('branch_updated', { name: next.name, region: next.region, sortOrder: next.sortOrder }, next.id);
  }, []);

  const archiveBranch = useCallback(async (id: string) => {
    const branch = branches.find((item) => item.id === id); if (!branch) return;
    const next = { ...branch, isActive: false, updatedAt: new Date().toISOString() };
    await putValue('branches', next); setBranches((list) => list.map((item) => item.id === id ? next : item));
    await recordEvent('branch_deactivated', { name: branch.name }, id);
  }, [branches]);

  const moveBranch = useCallback(async (id: string, delta: number) => {
    const list = activeBranches.slice(); const from = list.findIndex((item) => item.id === id); const to = from + delta;
    if (from < 0 || to < 0 || to >= list.length) return;
    [list[from], list[to]] = [list[to], list[from]];
    const updates = list.map((branch, index) => ({ ...branch, sortOrder: index + 1, updatedAt: new Date().toISOString() }));
    await Promise.all(updates.map((branch) => putValue('branches', branch)));
    setBranches((all) => all.map((branch) => updates.find((item) => item.id === branch.id) ?? branch));
  }, [activeBranches]);

  const saleFor = useCallback((branch: Branch, date = today) => sales.find((sale) => sale.branchId === branch.id && sale.salesDate === date) ?? emptySale(branch, date), [sales, today]);

  const saveSale = useCallback(async (branch: Branch, patch: Partial<DailySale>, _kind: 'sale_changed' | 'checklist_changed') => {
    const current = sales.find((sale) => sale.branchId === branch.id && sale.salesDate === today) ?? emptySale(branch, today);
    const next: DailySale = { ...current, ...patch, branchNameSnapshot: branch.name, regionSnapshot: branch.region, revision: current.revision + 1, updatedAt: new Date().toISOString() };
    await putValue('sales', next);
    setSales((list) => [...list.filter((sale) => !(sale.branchId === branch.id && sale.salesDate === today)), next]);
    // Daily inputs are local drafts. They are uploaded only by submitReport().
  }, [sales, today]);

  const resetToday = useCallback(async () => {
    const resets = activeBranches.map((branch) => {
      const existing = sales.find((sale) => sale.branchId === branch.id && sale.salesDate === today);
      return {
        ...emptySale(branch, today),
        id: existing ? existing.id : crypto.randomUUID(),
        revision: (existing?.revision ?? 0) + 1
      };
    });
    await Promise.all(resets.map((sale) => putValue('sales', sale)));
    setSales((list) => [...list.filter((sale) => sale.salesDate !== today), ...resets]);
    // Clearing a draft must never clear or overwrite an already submitted report.
  }, [activeBranches, sales, today]);

  const submitReport = useCallback(async (mode: ReportMode) => {
    const prepared = prepareReportSales(activeBranches, sales, today);
    if (prepared.missingBranchNames.length) {
      throw new ReportSubmissionError('incomplete', `กรอกยอดให้ครบทุกสาขาก่อนส่ง (${prepared.missingBranchNames.join(', ')})`);
    }
    if (!prepared.sales.length) throw new ReportSubmissionError('incomplete', 'ไม่มีสาขาสำหรับส่งรายงาน');

    const identity = await getDeviceIdentity();
    try {
      const result = await sendReport({ mode, salesDate: today, sales: prepared.sales, ...identity });
      await setSetting(reportSettingKey, '1');
      setReportSubmitted(true);
      await recordEvent(mode === 'create' ? 'report_submitted' : 'report_updated', {
        date: today,
        branchCount: prepared.sales.length
      });
      return result;
    } catch (error) {
      if (error instanceof ReportSubmissionError && error.code === 'conflict') {
        await setSetting(reportSettingKey, '1');
        setReportSubmitted(true);
      }
      throw error;
    }
  }, [activeBranches, reportSettingKey, sales, today]);

  const clearEverything = useCallback(async () => {
    await Promise.all(['branches', 'sales', 'outbox'].map(clearStore));
    await setSetting(reportSettingKey, '');
    setBranches([]); setSales([]); setReportSubmitted(false);
  }, [reportSettingKey]);

  return { ready, today, branches, activeBranches, sales, reportSubmitted, addBranch, updateBranch, archiveBranch, moveBranch, saleFor, saveSale, resetToday, submitReport, clearEverything };
}
